import React from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import useSWR from 'swr';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  currentUsageBillingKey,
  fetchUsageBilling,
  syncUsageBilling,
} from '../../features/billing';

export function UsagePaymentPrompt() {
  const { data: billing, mutate } = useSWR(currentUsageBillingKey, fetchUsageBilling);
  const [dismissed, setDismissed] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (location.pathname === '/billing') return;
    const params = new URLSearchParams(window.location.search);
    const checkoutSessionId = params.get('session_id');
    if (params.get('usage_billing') !== 'success' || !checkoutSessionId) return;
    void (async () => {
      try {
        await syncUsageBilling(checkoutSessionId);
        await mutate();
        params.delete('usage_billing');
        params.delete('session_id');
        window.history.replaceState(null, '', `${window.location.pathname}${params.size ? `?${params}` : ''}`);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Billing setup could not be confirmed.');
      }
    })();
  }, [mutate, location.pathname]);

  if (location.pathname === '/billing' || !billing || billing.paymentMethodPrompt === 'none' || billing.hasPaymentMethod) return null;
  const snapshot = billing;

  const oneRemaining = billing.paymentMethodPrompt === 'one_remaining';
  const paymentFailed = billing.paymentMethodPrompt === 'payment_failed';
  const title = paymentFailed
    ? 'Update your payment method'
    : oneRemaining
      ? 'One free session remaining'
      : 'Add a card to continue uploading';
  const body = paymentFailed
    ? 'Your latest payment could not be completed. Existing sessions remain available, but uploads are paused.'
    : oneRemaining
      ? 'You have used 4 of your 5 lifetime-free uploads. Add a card now to avoid an interruption after your final free session.'
      : 'You have used all 5 lifetime-free uploads. Existing sessions remain available, but a payment method is required before another upload.';

  function addPaymentMethod() {
    if (snapshot.canManageBilling) navigate('/billing');
  }

  return (
    <>
      <Alert
        severity={paymentFailed || !oneRemaining ? 'warning' : 'info'}
        action={snapshot.canManageBilling ? <Button color="inherit" size="small" onClick={addPaymentMethod}>Open Billing</Button> : undefined}
      >
        {title}
      </Alert>
      <Dialog open={!dismissed} onClose={() => setDismissed(true)} maxWidth="sm" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography color="text.secondary">{body}</Typography>
            <Typography>
              Paddiq is $9/month including 5 sessions each month. Additional sessions are $1 each.
            </Typography>
            {!snapshot.canManageBilling ? (
              <Alert severity="info">Ask a {snapshot.ownerLabel} billing administrator to add the team payment method.</Alert>
            ) : null}
            {error ? <Alert severity="error">{error}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDismissed(true)}>Continue read-only</Button>
          {snapshot.canManageBilling ? (
            <Button variant="contained" onClick={addPaymentMethod}>Open Billing</Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </>
  );
}
